import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import { toast } from "react-toastify";
import { FaEdit, FaSearch, FaRedo } from "react-icons/fa";

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: "rgb(23, 44, 87)",
  primaryLight: "rgba(23, 44, 87, 0.08)",
};

const TeamLeadEmployeeData = () => {
  const { employee_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [employeeName, setEmployeeName] = useState("");
  const [loading, setLoading] = useState(false);

  // Filter States
  const [filterStatus, setFilterStatus] = useState("Interested");
  const [districtFilter, setDistrictFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [memberType, setMemberType] = useState(null);

  useEffect(() => {
    const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
    const decodedMemberType = encodedMemberType ? atob(encodedMemberType) : null;
    setMemberType(decodedMemberType);
  }, []);

  const isTeamLead = useMemo(() => memberType === "Team Lead", [memberType]);

  // Reset pagination whenever any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, districtFilter, dateFilter, startDate, endDate, searchQuery]);

  useEffect(() => {
    const fetchEmployeeStudentData = async () => {
      setLoading(true);
      try {
        const teamLeadEmpId = atob(sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6"));

        const response = await fetch(SummaryApi.getEmpDataByTLIdndEmpId.url, {
          method: SummaryApi.getEmpDataByTLIdndEmpId.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify({
            team_lead_emp_id: teamLeadEmpId,
            assign_emp_id: employee_id,
          }),
        });

        if (!response.ok) throw new Error("Failed to fetch student data");

        const data = await response.json();
        setStudents(data?.data || []);

        if (data?.data && data.data.length > 0) {
          setEmployeeName(data.data[0].employee_name || "Unknown Employee");
        } else {
          setEmployeeName("Unknown Employee");
        }
      } catch (error) {
        toast.error(`Error fetching data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeStudentData();
  }, [employee_id]);

  // Robust date parsing (handles YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, ISO, and SQL datetime)
  const parseDate = (dateVal) => {
    if (!dateVal) return null;
    if (dateVal instanceof Date && !isNaN(dateVal.getTime())) return dateVal;

    if (typeof dateVal === "string") {
      const clean = dateVal.trim();

      // Check DD-MM-YYYY or DD/MM/YYYY
      const ddmmyyyy = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/);
      if (ddmmyyyy) {
        const [, day, month, year, hours = 0, mins = 0, secs = 0] = ddmmyyyy;
        return new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(mins), Number(secs));
      }

      // Check YYYY-MM-DD with or without time
      const yyyymmdd = clean.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/);
      if (yyyymmdd) {
        const [, year, month, day, hours = 0, mins = 0, secs = 0] = yyyymmdd;
        return new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(mins), Number(secs));
      }

      const parsed = new Date(clean);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return null;
  };

  const filterByDateRange = (studentDate) => {
    if (dateFilter === "All") return true;
    if (!studentDate) return false;

    const date = parseDate(studentDate);
    if (!date) return false;

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

    switch (dateFilter) {
      case "Today":
        return date >= startOfToday && date <= endOfToday;

      case "Yesterday": {
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        const endOfYesterday = new Date(startOfYesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        return date >= startOfYesterday && date <= endOfYesterday;
      }

      case "This Week": {
        // Starts on Sunday of current week
        const startOfWeek = new Date(startOfToday);
        startOfWeek.setDate(startOfToday.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        return date >= startOfWeek && date <= endOfWeek;
      }

      case "Last Week": {
        const startOfLastWeek = new Date(startOfToday);
        startOfLastWeek.setDate(startOfToday.getDate() - today.getDay() - 7);
        const endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
        endOfLastWeek.setHours(23, 59, 59, 999);
        return date >= startOfLastWeek && date <= endOfLastWeek;
      }

      case "This Month":
        return (
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear()
        );

      case "Last Month": {
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const targetYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        const targetMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        return date.getMonth() === targetMonth && date.getFullYear() === targetYear;
      }

      case "Custom": {
        let valid = true;
        if (startDate) {
          const [sY, sM, sD] = startDate.split("-").map(Number);
          const start = new Date(sY, sM - 1, sD, 0, 0, 0, 0);
          valid = valid && date >= start;
        }
        if (endDate) {
          const [eY, eM, eD] = endDate.split("-").map(Number);
          const end = new Date(eY, eM - 1, eD, 23, 59, 59, 999);
          valid = valid && date <= end;
        }
        return valid;
      }

      default:
        return true;
    }
  };

  const counts = useMemo(() => {
    const allCount = students.length;
    const mostInterestedCount = students.filter(
      (s) => s.is_interested?.trim().toLowerCase() === "most interested"
    ).length;
    const interestedCount = students.filter(
      (s) => s.is_interested?.trim().toLowerCase() === "interested"
    ).length;
    return { allCount, mostInterestedCount, interestedCount };
  }, [students]);

  const districts = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      if (s.district && s.district.trim()) {
        set.add(s.district.trim());
      }
    });
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [students]);

  const filteredStudents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return students.filter((student) => {
      // 1. Status Filter
      const studentStatus = student.is_interested?.trim().toLowerCase();
      const statusMatch =
        filterStatus === "All" ||
        studentStatus === filterStatus.toLowerCase();

      // 2. District Filter
      const studentDistrict = student.district?.trim().toLowerCase();
      const districtMatch =
        districtFilter === "All" ||
        studentDistrict === districtFilter.toLowerCase();

      // 3. Date Filter
      const dateMatch = filterByDateRange(student.created_date);

      // 4. Search Filter
      const searchMatch =
        q === "" ||
        (student.student_name && student.student_name.toLowerCase().includes(q)) ||
        (student.mobile_number1 && String(student.mobile_number1).includes(q)) ||
        (student.city && student.city.toLowerCase().includes(q)) ||
        (student.clg_name && student.clg_name.toLowerCase().includes(q)) ||
        (student.district && student.district.toLowerCase().includes(q)) ||
        (student.employee_name && student.employee_name.toLowerCase().includes(q)) ||
        (student.student_id && String(student.student_id).toLowerCase().includes(q));

      return statusMatch && districtMatch && dateMatch && searchMatch;
    });
  }, [students, filterStatus, districtFilter, dateFilter, startDate, endDate, searchQuery]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredStudents.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / recordsPerPage));

  const handlePrev = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  const handleNext = () => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));

  const resetAllFilters = () => {
    setFilterStatus("All");
    setDistrictFilter("All");
    setDateFilter("All");
    setStartDate("");
    setEndDate("");
    setSearchQuery("");
  };

  const isFiltered =
    filterStatus !== "All" ||
    districtFilter !== "All" ||
    dateFilter !== "All" ||
    searchQuery.trim() !== "" ||
    startDate !== "" ||
    endDate !== "";

  const handleEditClick = async (student) => {
    try {
      const responseDetails = await fetch(
        SummaryApi.getStudentDataByStudentId.url,
        {
          method: SummaryApi.getStudentDataByStudentId.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify({ student_id: student.student_id }),
        }
      );
      const studentData = (await responseDetails.json()).data || null;

      let followUps = [];
      if (student.tl_student_followup_id) {
        const responseFollow = await fetch(
          SummaryApi.getStudentFollowDetails.url,
          {
            method: SummaryApi.getStudentFollowDetails.method,
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer sandeep_university_916",
            },
            body: JSON.stringify({
              tl_student_followup_id: student.tl_student_followup_id,
            }),
          }
        );
        const followData = await responseFollow.json();
        followUps = followData.data || [];
      }

      navigate("/my-history", {
        state: {
          student,
          studentDetails: studentData,
          studentFollowUps: followUps,
        },
      });
    } catch (err) {
      toast.error("Failed to load followup history");
    }
  };

  return (
    <div className="min-h-screen">
      {/* --- Header --- */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-4 mb-5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: THEME.primary }}>
              Assigned Students
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Employee:{" "}
              <span className="font-semibold" style={{ color: THEME.primary }}>
                {employeeName}
              </span>
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            style={{ color: THEME.primary }}
            className="bg-white border border-gray-300 px-4 py-2 rounded-lg shadow-sm hover:bg-gray-50 transition font-semibold text-sm"
          >
            Back
          </button>
        </div>
      </div>

      {/* --- Status Tabs --- */}
      <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
        {["All", "Most Interested", "Interested"].map((status) => {
          const isActive = filterStatus === status;
          return (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={
                isActive
                  ? { backgroundColor: THEME.primary, borderColor: THEME.primary }
                  : { color: THEME.primary }
              }
              className={`px-4 py-2 rounded-lg border text-sm font-semibold transition whitespace-nowrap ${isActive ? "text-white shadow-md" : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
            >
              {status}{" "}
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold"
                style={
                  isActive
                    ? { backgroundColor: "white", color: THEME.primary }
                    : { backgroundColor: THEME.primaryLight, color: THEME.primary }
                }
              >
                {status === "All"
                  ? counts.allCount
                  : status === "Most Interested"
                    ? counts.mostInterestedCount
                    : counts.interestedCount}
              </span>
            </button>
          );
        })}
      </div>

      {/* --- Unified Filter Toolbar --- */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search Name, Mobile, City, College..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-transparent transition"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* District Filter */}
            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#172C57] min-w-[160px] cursor-pointer font-medium"
            >
              {districts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist === "All" ? "All Districts" : dist}
                </option>
              ))}
            </select>

            {/* Date Filter Dropdown */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#172C57] min-w-[140px] cursor-pointer font-medium"
            >
              <option value="All">All Time</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
              <option value="Last Week">Last Week</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="Custom">Custom Range</option>
            </select>

            {/* Custom Date Inputs */}
            {dateFilter === "Custom" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                />
                <span className="text-gray-400 font-medium">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                />
              </div>
            )}

            {/* Reset Filters Button */}
            {isFiltered && (
              <button
                onClick={resetAllFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition"
                title="Reset all filters"
              >
                <FaRedo className="text-xs" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Table --- */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>SNO</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Student Name</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>District</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>City</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>College Name</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Mobile Number</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Enrolled Date</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Current Status</th>
              <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Lead By</th>
              {isTeamLead && (
                <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isTeamLead ? "10" : "9"} className="text-center py-8">
                  <div
                    className="inline-block animate-spin rounded-full h-8 w-8 border-b-2"
                    style={{ borderColor: THEME.primary }}
                  ></div>
                  <p className="mt-2 text-gray-500">Loading data...</p>
                </td>
              </tr>
            ) : currentRecords.length === 0 ? (
              <tr>
                <td colSpan={isTeamLead ? "10" : "9"} className="text-center py-8 text-gray-500">
                  <div className="flex flex-col items-center">
                    <FaSearch className="text-gray-300 text-3xl mb-2" />
                    <p>No data found matching your criteria.</p>
                  </div>
                </td>
              </tr>
            ) : (
              currentRecords.map((student, index) => (
                <tr
                  key={student.student_id || index}
                  className={`border-b border-gray-100 transition duration-150 ${student.next_followup_date_time
                    ? "bg-green-50 hover:bg-green-100"
                    : index % 2 === 0
                      ? "bg-white hover:bg-gray-50"
                      : "bg-gray-50 hover:bg-gray-100"
                    }`}
                >
                  <td className="py-3 px-4 text-gray-600">{indexOfFirstRecord + index + 1}</td>
                  <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                    {student.student_name || "-"}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.district || "-"}</td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.city || "-"}</td>
                  <td className="py-3 px-4 max-w-xs truncate" title={student.clg_name}>
                    {student.clg_name || "-"}
                  </td>
                  <td className="py-3 px-4 font-mono text-gray-600 whitespace-nowrap" title={student.mobile_number1}>
                    {student.mobile_number1 || "-"}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.created_date || "-"}</td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${student.is_interested?.trim().toLowerCase() === "most interested"
                        ? "bg-green-100 text-green-700"
                        : student.is_interested?.trim().toLowerCase() === "interested"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-200 text-gray-700"
                        }`}
                    >
                      {student.is_interested || "-"}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.employee_name || "-"}</td>
                  {isTeamLead && (
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        style={{ backgroundColor: THEME.primary }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                        className="text-white px-3 py-1.5 rounded text-xs font-semibold transition-opacity shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                        onClick={() => handleEditClick(student)}
                      >
                        <FaEdit className="shrink-0" />
                        <span>Followup</span>
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination --- */}
      {filteredStudents.length > recordsPerPage && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            style={currentPage !== 1 ? { color: THEME.primary } : {}}
            className={`px-3 py-1.5 border rounded-lg text-sm font-semibold transition ${currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white hover:bg-gray-50 border-gray-300"
              }`}
          >
            Previous
          </button>

          <span
            className="px-4 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
            style={{ backgroundColor: THEME.primary }}
          >
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            style={currentPage !== totalPages ? { color: THEME.primary } : {}}
            className={`px-3 py-1.5 border rounded-lg text-sm font-semibold transition ${currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white hover:bg-gray-50 border-gray-300"
              }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamLeadEmployeeData;